import type { LeadStatus, LeadActivityType } from '@/app/types/lead';

/** Badge variant per pipeline stage. Kept in one place so list and detail agree. */
export const statusVariant: Record<
    LeadStatus,
    'default' | 'secondary' | 'success' | 'warning' | 'destructive'
> = {
    new: 'secondary',
    contacted: 'default',
    interested: 'warning',
    converted: 'success',
    lost: 'destructive',
};

/** Stages a rep can move a lead to from the app. `converted` is excluded on purpose — it only happens through the convert flow, which creates the party. */
export const SELECTABLE_STATUSES: LeadStatus[] = ['new', 'contacted', 'interested', 'lost'];

export const activityIconName: Record<LeadActivityType, string> = {
    note: 'FileText',
    call: 'Phone',
    visit: 'MapPin',
    whatsapp: 'MessageCircle',
    status_change: 'ArrowRight',
    follow_up: 'CalendarClock',
    conversion: 'CheckCircle2',
    photo: 'Camera',
};

/** Local YYYY-MM-DD. Never use toISOString() — that shifts to UTC and can name yesterday. */
export const toLocalISODate = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const todayISO = (): string => toLocalISODate(new Date());

export const addDaysISO = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return toLocalISODate(d);
};

/** "Today" / "Overdue by 3 days" / "12 Aug" — what a rep actually needs to read. */
export const formatFollowUp = (dateStr?: string | null): { label: string; overdue: boolean } => {
    if (!dateStr) return { label: 'No follow-up set', overdue: false };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(`${dateStr}T00:00:00`);
    const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

    if (diffDays === 0) return { label: 'Due today', overdue: false };
    if (diffDays === 1) return { label: 'Due tomorrow', overdue: false };
    if (diffDays < 0) {
        const n = Math.abs(diffDays);
        return { label: `Overdue by ${n} day${n === 1 ? '' : 's'}`, overdue: true };
    }
    return {
        label: target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        overdue: false,
    };
};

export const formatRelativeTime = (iso?: string | null): string => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const mins = Math.round((Date.now() - then) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// whatsappUrl moved to @/app/lib/phone — parties share it now.
